import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  phqAssessments: [],
  currentPhqAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all PHQ Assessments
export const fetchPhqAssessments = createAsyncThunk(
  'phqAssessments/fetchPhqAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/phq-assessments?patient_id=${patientId}`
        : '/phq-assessments';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch PHQ assessments'
      );
    }
  }
);

// Fetch single PHQ Assessment
export const fetchPhqAssessment = createAsyncThunk(
  'phqAssessments/fetchPhqAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/phq-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch PHQ assessment'
      );
    }
  }
);

// Create PHQ Assessment
export const createPhqAssessment = createAsyncThunk(
  'phqAssessments/createPhqAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/phq-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create PHQ assessment'
      );
    }
  }
);

// Update PHQ Assessment
export const updatePhqAssessment = createAsyncThunk(
  'phqAssessments/updatePhqAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/phq-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update PHQ assessment'
      );
    }
  }
);

// Delete PHQ Assessment
export const deletePhqAssessment = createAsyncThunk(
  'phqAssessments/deletePhqAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/phq-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete PHQ assessment'
      );
    }
  }
);

// ==================== Slice ====================
const phqAssessmentsSlice = createSlice({
  name: 'phqAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPhqAssessment: (state, action) => {
      state.currentPhqAssessment = action.payload;
    },
    clearCurrentPhqAssessment: (state) => {
      state.currentPhqAssessment = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchPhqAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPhqAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.phqAssessments = action.payload;
      })
      .addCase(fetchPhqAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchPhqAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPhqAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPhqAssessment = action.payload;
      })
      .addCase(fetchPhqAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createPhqAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPhqAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.phqAssessments.unshift(action.payload);
      })
      .addCase(createPhqAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updatePhqAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePhqAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.phqAssessments.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.phqAssessments[index] = updated;
        }

        if (state.currentPhqAssessment?.id === updated.id) {
          state.currentPhqAssessment = updated;
        }
      })
      .addCase(updatePhqAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deletePhqAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePhqAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        state.phqAssessments = state.phqAssessments.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentPhqAssessment?.id === action.payload) {
          state.currentPhqAssessment = null;
        }
      })
      .addCase(deletePhqAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentPhqAssessment,
  clearCurrentPhqAssessment,
} = phqAssessmentsSlice.actions;

export default phqAssessmentsSlice.reducer;
